import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI, API_MISCONFIGURED } from '../lib/api';
import { posthogIdentify, posthogReset, posthogCapture } from '../posthog.config';
import { setSentryUser } from '../sentry.config';
import { adIdentify } from '../lib/adPixels';
import { getAcquisition } from '../lib/acquisition';

const AuthContext = createContext(null);

// SECURITY (v5.7.35): Auth is COOKIE-ONLY.
//
// The JWT lives exclusively in the HttpOnly, Secure, SameSite=Lax
// `fantasy_token` cookie. We no longer mirror it into localStorage; the
// previous implementation defeated the HttpOnly defence because any XSS
// could read the localStorage copy and re-attach it as a Bearer header.
//
// Consequences for the UI:
//   - There is no longer a `token` value to read on the client.
//   - Cross-tab session sync still works because the cookie is shared.
//   - On boot we ALWAYS call /auth/me to discover the user; if the cookie
//     is valid we get a user, otherwise we treat the visitor as anonymous.
//   - We also proactively purge any stale localStorage keys from the
//     old scheme so they can't be scraped later.

// v6.1.5 Phase G5 A.1 — Module-level constant so the useEffect
// dependency array can stay empty.  5-min hidden window is a fixed
// product decision (not a tunable runtime knob).
const VISIBILITY_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  // Advanced matching — hand the session user's id/email to the ad pixels
  // (both hash PII client-side). Guests have no email and are skipped.
  useEffect(() => {
    if (user?.id && !user?.is_guest) adIdentify({ id: user.id, email: user.email });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loading, setLoading] = useState(true);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  useEffect(() => {
    // One-time hygiene: remove any localStorage leftovers from the old
    // localStorage-JWT scheme. Idempotent.
    try {
      localStorage.removeItem('fantasy_token');
      localStorage.removeItem('fantasy_user');
    } catch { /* private mode */ }

    let mounted = true;

    // v5.7.79 — Cold-reload silent refresh.
    // The axios 401-interceptor handles MID-SESSION rotation perfectly
    // (any business-API 401 → silent /refresh + retry). But it explicitly
    // EXCLUDES /auth/me from triggering a refresh to avoid an infinite
    // loop (the interceptor calls /auth/me, which 401s, which triggers
    // refresh, which… etc.). That means a user returning AFTER the 1h
    // access TTL but inside the 7d refresh window would get bounced to /
    // on first load.
    //
    // Fix: try ONE optimistic /auth/refresh at mount. If it succeeds,
    // /auth/me will now see fresh tokens and the user stays logged in.
    // If the refresh 401s (no refresh cookie, expired refresh, family
    // revoked), it's a no-op and /auth/me proceeds as before.
    //
    // We swallow the refresh error — the only signal that matters is
    // whether /auth/me succeeds afterwards.
    const bootstrap = async () => {
      try {
        await authAPI.refresh();
      } catch {
        // No refresh cookie / expired / revoked — proceed to /me which
        // will return 401 and we'll treat the visitor as anonymous.
      }
      try {
        const res = await authAPI.me();
        if (mounted) {
          setUser(res.data.user);
          // Re-link analytics to the hydrated session on cold reload.
          posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
          setSentryUser(res.data.user?.id, res.data.user?.username);
          // v6.1.17.7-hotfix-N1 — proactive username-picker mount.
          // The 403 interceptor in `lib/api.js` handles the gate-fired
          // case, but a returning user with `username_legacy: true`
          // should see the picker IMMEDIATELY on session hydrate
          // rather than after their first failing API call.  Listen
          // here on the SAME event the interceptor uses so the global
          // `UsernamePickerHost` is the single mount point.
          if (res.data.user?.requires_username && typeof window !== 'undefined') {
            try {
              window.dispatchEvent(new CustomEvent('fws:username-required', {
                detail: { reason: { username_legacy: !!res.data.user.username_legacy }, source: 'me-hydrate' },
              }));
            } catch { /* ignore */ }
          }
          try { sessionStorage.setItem('fws_session_active', '1'); }
          catch { /* private-mode / quota — ignore */ }
        }
      } catch {
        if (mounted) {
          setUser(null);
          // v6.1.4 P0 — Anonymous visitor: ensure the flag is NOT set
          // (in case a previous session left it).  An unauthenticated
          // visitor on a returning tab should see the splash on every
          // load, not have it suppressed.
          try { sessionStorage.removeItem('fws_session_active'); }
          catch { /* ignore */ }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    bootstrap();

    return () => { mounted = false; };
  }, []);

  // v6.1.5 Phase G5 A.1 — Tab-visibility silent /auth/me refresh.
  //
  // When the user backgrounds the tab for >5 min and comes back, do a
  // SILENT /auth/me to validate the session is still live BEFORE they
  // tap a button that would 401.  Three outcomes:
  //   - 200 → session valid, no UI change.
  //   - 401 → session expired/revoked; clear fws_session_active + null
  //           the user so the next render kicks them to the landing
  //           page.  The axios interceptor handles the redirect.
  //   - network error → silent (treat as "unknown" — don't disrupt the
  //                       user, they'll find out on their next call).
  //
  // Threshold: 5 minutes of HIDDEN time.  Lower than that and we'd
  // hammer /auth/me on every brief blur/focus (tab-switching is fast).
  // Higher than that and users who alt-tab back to a long-stale tab
  // get a confusing 401 on their first action.  5 min matches the
  // typical refresh-token half-life heuristic without burning quota.
  const hiddenAtRef = useRef(null);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      // document.visibilityState === 'visible' (or 'prerender').
      if (!hiddenAtRef.current) return;
      const hiddenMs = Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenMs < VISIBILITY_REFRESH_THRESHOLD_MS) return;
      // Only bother if we think the user IS logged in — anonymous
      // visitors don't have a session to validate.
      let sessionActive = false;
      try { sessionActive = sessionStorage.getItem('fws_session_active') === '1'; }
      catch { /* ignore */ }
      if (!sessionActive) return;
      // AUDIT 2026-06 (P1): the 401-interceptor EXCLUDES /auth/me from its
      // silent refresh (api.js `_shouldAttemptRefresh`) to avoid a loop, so a
      // bare /auth/me here would 401 — and wrongly log out — a returning user
      // whose ACCESS token expired during the hidden window but whose REFRESH
      // token is still valid. Mirror bootstrap(): attempt ONE /auth/refresh
      // first, THEN /auth/me. Only a genuinely dead session reaches the catch.
      (async () => {
        try { await authAPI.refresh(); }
        catch { /* no/expired/revoked refresh — /auth/me will 401 below */ }
        try {
          await authAPI.me();
          /* still authed — nothing to do */
        } catch (err) {
          if (err?.response?.status === 401) {
            try { sessionStorage.removeItem('fws_session_active'); } catch {}
            setUser(null);
            setSentryUser(null);
            // By nulling user, the next render shows the landing page if the
            // route demands auth (the interceptor handles any route bounce).
          }
          // Network / non-401 errors → silent.
        }
      })();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const login = async (email, password) => {
    // v5.8.36 (Q1 follow-up) — Surface env-misconfig as a precise error
    // so the user doesn't see a generic "Login failed" toast and waste
    // hours debugging the wrong layer.
    if (API_MISCONFIGURED) {
      const err = new Error(
        'Backend URL not configured — please copy frontend/.env.example to frontend/.env and restart the dev server.'
      );
      err.misconfigured = true;
      throw err;
    }
    const res = await authAPI.login({ email, password });
    // v5.7.80 — If MFA is enabled on the account, the backend returns
    // {mfa_required: true, mfa_challenge_token} instead of the auth
    // cookies + user object. The caller (AuthPage) checks for this
    // and renders the MFA challenge step. We return the body as-is
    // so AuthPage can route on `mfa_required`.
    if (res.data?.mfa_required) {
      return res.data;
    }
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    // v6.1.4 P0 — Mark this tab's session active so a same-tab reload
    // skips the splash + DevNews modal replay.
    try { sessionStorage.setItem('fws_session_active', '1'); }
    catch { /* private-mode / quota — ignore */ }
    return res.data;
  };

  const verifyMfa = async ({ mfa_challenge_token, code, backup_code }) => {
    const res = await authAPI.mfaVerify({ mfa_challenge_token, code, backup_code });
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    try { sessionStorage.setItem('fws_session_active', '1'); } catch {}
    return res.data;
  };

  const register = async (email, username, password, turnstileToken) => {
    const res = await authAPI.register(
      { email, username, password, acquisition: getAcquisition() },
      turnstileToken,
    );
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    // 2026-07 — web parity with mobile's funnel taxonomy (ANALYTICS_EVENTS.md).
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    posthogCapture('signup', { method: 'email' });
    try { sessionStorage.setItem('fws_session_active', '1'); } catch {}
    setIsFirstLogin(true);
    return res.data;
  };

  const guestLogin = async (turnstileToken) => {
    const res = await authAPI.guest(turnstileToken);
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    posthogCapture('signup', { method: 'guest' });
    try { sessionStorage.setItem('fws_session_active', '1'); } catch {}
    setIsFirstLogin(true);
    return res.data;
  };

  // 2026-08-11 — social login. Same session contract as password login
  // (cookies set by the server) when a session is returned. `idToken` is the
  // provider's ID/identity token. When the email already belongs to a
  // password account the server returns {link_required, confirmation_token}
  // (NO session) — we surface that to the caller unchanged.
  const oauthLogin = async (provider, idToken) => {
    const res = await authAPI.oauthLogin(provider, idToken);
    if (res.data?.link_required) return res.data;
    // 2026-08-13 audit SEC-001 — MFA-enabled accounts get a challenge
    // instead of a session; surface it so the caller renders the MFA step.
    if (res.data?.mfa_required) return res.data;
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    posthogCapture('signup', { method: provider });
    try { sessionStorage.setItem('fws_session_active', '1'); } catch {}
    return res.data;
  };

  // 2026-09-17 — "Continue with Google instead": the email is a provider-hosted
  // mailbox, so the server links WITHOUT the password (and disables the old one).
  const oauthLinkVerified = async (confirmationToken, provider) => {
    const res = await authAPI.oauthLinkVerified(confirmationToken);
    if (res.data?.mfa_required) return res.data;
    setUser(res.data.user);
    setSentryUser(res.data.user?.id, res.data.user?.username);
    posthogIdentify(res.data.user?.id, { username: res.data.user?.username });
    posthogCapture('login', { method: provider, verified_link: true });
    try { sessionStorage.setItem('fws_session_active', '1'); } catch {}
    return res.data;
  };

  const logout = async () => {
    try { await authAPI.logout(); }
    catch (e) { console.error('Logout API error:', e); }
    setUser(null);
    setSentryUser(null);
    // Sever analytics identity so post-logout events aren't attributed
    // to the previous user (parity with mobile AuthContext.logout).
    posthogReset();
    // v6.1.4 P0 — Clear the session-active flag so the NEXT login
    // attempt sees the splash again (user expectation after explicit
    // logout: "start fresh").
    try { sessionStorage.removeItem('fws_session_active'); } catch {}
  };

  // v5.7.61 — Refresh the cached user doc (e.g. after a Stripe payment
  // flips `premium = true`). Idempotent; failure leaves `user` intact.
  const refreshUser = async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data.user);
      return res.data.user;
    } catch (e) {
      console.error('refreshUser failed:', e);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      // Kept for components that still destructure `token` — always null now.
      token: null,
      loading,
      login,
      oauthLogin,
      oauthLinkVerified,
      verifyMfa,
      register,
      guestLogin,
      logout,
      refreshUser,
      isFirstLogin,
      clearFirstLogin: () => setIsFirstLogin(false),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
